'use client'

import { useId } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { cn } from '@/lib/utils'
import { isPersonaId, personaById, usePersona } from '@/lib/persona'
import { canSee, pageIdForPath } from '@/lib/access'
import { toast } from '@/components/ui/toast'
import { usePersonaRoster } from '@/hooks/useShellData'

/**
 * "Viewing as" (plans/03 A5.2; artifact `50-core.js` persona select). A native
 * select keeps keyboard and screen reader behaviour for free. Changing it
 * re-filters the rail, sets `X-AIO-Persona` on every request and, when the
 * current page is not in the new persona's role, goes to the Console.
 */
export function PersonaMenu({ className, showLabel = false }: { className?: string; showLabel?: boolean }) {
  const { persona, pinned, setPersona, personas } = usePersona()
  const router = useRouter()
  const pathname = usePathname()
  const id = useId()
  usePersonaRoster()

  const onChange = (value: string) => {
    if (!isPersonaId(value) || value === persona) return
    setPersona(value)
    const p = personaById(value)
    toast.success(value === 'supervity-support' ? `Viewing as ${p.short} (all pages)` : `Viewing as ${p.short}`)
    const page = pageIdForPath(pathname)
    if (page && !canSee(value, page)) router.push('/')
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <label
        htmlFor={id}
        className={cn('whitespace-nowrap text-xs font-medium text-muted-foreground', showLabel ? 'inline' : 'hidden xl:inline')}
      >
        Viewing as
      </label>
      <select
        id={id}
        aria-label={showLabel ? undefined : 'Viewing as'}
        value={persona}
        disabled={pinned}
        title={pinned ? 'The Presenter works as Supervity support' : 'Viewing as'}
        onChange={(event) => onChange(event.target.value)}
        className={cn(
          showLabel ? 'w-full' : 'max-w-[9rem] xl:max-w-[13rem]',
          'h-9 truncate rounded-xl border border-border bg-card px-3 text-sm text-foreground',
          'transition-colors hover:border-brand-cornflower',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'disabled:cursor-not-allowed disabled:opacity-70'
        )}
      >
        {personas.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
    </div>
  )
}
